import { LucideCopy, ThumbsDown, ThumbsUp } from 'lucide-react';
import Showdown from 'showdown';

import IconButton from '@/components/Button/IconButton';
import AMP_KEYS from '@/configs/amp-track-keys';
import { cn } from '@/lib/cn';
import useHeySynclyStore from '@/stores/HeySynclyStore';
import { toast } from '@/stores/pure/ToastStore';
import { track } from '@/utils/track/track';

import { stripCitations } from '../../lib/citation';

const converter = new Showdown.Converter({
  tables: true,
  literalMidWordUnderscores: true,
  simpleLineBreaks: true,
  disableForced4SpacesIndentedSublists: true,
});

export const MessageActions = (props: {
  text: string;
  chat_id: Alias;
  message_id: Alias;
  vote_up: boolean | null;
  prompt: string;
  hideVote?: boolean;
  additionalAction?: ({
    chat_id,
    question,
  }: {
    chat_id: string;
    question?: string;
  }) => React.ReactNode;
}) => {
  const voteMessage = useHeySynclyStore((state) => state.voteMessage);
  const { text, chat_id: chat_alias, message_id, vote_up, hideVote } = props;
  const handleCopy = async () => {
    const markdownString = stripCitations(text);
    const htmlString = converter.makeHtml(markdownString);

    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([htmlString], { type: 'text/html' }),
        'text/plain': new Blob([markdownString], { type: 'text/plain' }),
      }),
    ]);

    void track(AMP_KEYS.HEY_SYNCLY_ANSWER_ACTIONS, {
      action: 'copy',
      prompt: prompt,
    });
    toast('Copied to clipboard', 'success');
  };

  const isUpVoted = vote_up === true;
  const isDownVoted = vote_up === false;

  const handleThumbsUp = () => {
    const newVote = isUpVoted ? null : true;

    void voteMessage({
      chat_alias: chat_alias,
      message_id: message_id,
      vote: newVote,
    });

    // Track vote event
    if (newVote !== null) {
      void track(AMP_KEYS.HEY_SYNCLY_ANSWER_ACTIONS, {
        prompt: prompt,
        action: 'vote_up',
      });
    }

    toast('Thank you for your feedback!', 'success');
  };

  const handleThumbsDown = () => {
    const newVote = isDownVoted ? null : false;

    void voteMessage({
      chat_alias: chat_alias,
      message_id: message_id,
      vote: newVote,
    });

    // Track vote event
    if (newVote !== null) {
      void track(AMP_KEYS.HEY_SYNCLY_ANSWER_ACTIONS, {
        prompt: prompt,
        action: 'vote_down',
      });
    }

    toast('Thank you for your feedback!', 'success');
  };

  return (
    <div className="flex text-gray-700 text-sm items-center px-2 -mx-2 mt-2 justify-between">
      <div className="flex items-center gap-0.5">
        <IconButton
          sizeType="m"
          outlined={false}
          icon={<LucideCopy className="size-4 text-gray-600" />}
          onClick={handleCopy}
        />
        {!hideVote && (
          <>
            <IconButton
              sizeType="m"
              outlined={false}
              icon={
                <ThumbsUp
                  className={cn(
                    'size-4 text-gray-600',
                    isUpVoted && 'text-green-500',
                  )}
                />
              }
              onClick={handleThumbsUp}
            />
            <IconButton
              sizeType="m"
              outlined={false}
              icon={
                <ThumbsDown
                  className={cn(
                    'size-4 text-gray-600',
                    isDownVoted && 'text-red-500',
                  )}
                />
              }
              onClick={handleThumbsDown}
            />
          </>
        )}
      </div>
      {props.additionalAction &&
        props.additionalAction({ chat_id: chat_alias, question: props.prompt })}
    </div>
  );
};
